import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Loader2, Shield, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const INTERNAL_ROLES = {
  admin: {
    id: 'admin',
    name: 'Administrator',
    description: 'Full platform access — manage events, games, users, and system settings',
    icon: Shield,
    color: 'rooam-red',
    redirectTo: '/admin',
  },
  creator: {
    id: 'creator',
    name: 'Creator',
    description: 'Tournament directors, event directors, and content creators — stream and monetize your events',
    icon: Video,
    color: 'rooam-blue',
    redirectTo: '/dashboard',
  },
  operator: {
    id: 'operator',
    name: 'Operator',
    description: 'Game operators — run the scoring console for live games and tournaments',
    icon: Video,
    color: 'rooam-blue',
    redirectTo: '/operator',
  },
};

const InternalRegister = () => {
  const { roleType } = useParams();
  const role = INTERNAL_ROLES[roleType];
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  if (!role) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Access</h2>
          <Link to="/" className="text-rooam-blue hover:underline text-sm">Back to Home</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(formData.name, formData.email, formData.password, role.id);
    if (result.success) {
      toast.success('Account Created!', { description: `Welcome — ${role.name} access activated` });
      navigate(role.redirectTo);
    } else {
      toast.error('Registration Failed', { description: result.error });
    }
    setLoading(false);
  };

  const Icon = role.icon;
  const isAdmin = roleType === 'admin';

  return (
    <div className="min-h-screen bg-rooam-black flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img src="/rooam-logo.png" alt="Rooam Sports Network" className="h-12 w-auto" />
        </Link>

        <Card className={`bg-rooam-gray-medium shadow-2xl ${isAdmin ? 'border-rooam-red/50' : 'border-rooam-gray-light'}`}>
          <CardHeader className="text-center">
            <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center ${isAdmin ? 'bg-rooam-red/10' : 'bg-rooam-blue/10'}`}>
              <Icon className={`h-7 w-7 ${isAdmin ? 'text-rooam-red' : 'text-rooam-blue'}`} />
            </div>
            <CardTitle className="text-xl text-white">{role.name} Registration</CardTitle>
            <p className="text-gray-400 text-sm">{role.description}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    type="text" placeholder="Full name"
                    className="pl-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="internal-register-name" required
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    type="email" placeholder="your@email.com"
                    className="pl-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="internal-register-email" required
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    type="password" placeholder="••••••••"
                    className="pl-10 bg-rooam-black border-rooam-gray-light text-white placeholder-gray-500"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="internal-register-password" required
                  />
                </div>
              </div>
              <Button
                type="submit"
                className={`w-full font-semibold ${isAdmin ? 'bg-rooam-red hover:bg-red-600 text-white' : 'bg-rooam-blue hover:bg-rooam-blue-light text-black'}`}
                disabled={loading}
                data-testid="internal-register-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Create ${role.name} Account`}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-rooam-blue hover:text-rooam-blue-light font-medium">Sign in</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InternalRegister;
